export type EllipsisPageItem = number | "ellipsis";

/**
 * Compact pagination with ellipses (settings users, data-table, arbitrator table).
 */
export function buildEllipsisPages(
  page: number,
  totalPages: number,
): EllipsisPageItem[] {
  const pages: EllipsisPageItem[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return pages;
  }
  pages.push(1);
  if (page > 3) pages.push("ellipsis");
  for (
    let i = Math.max(2, page - 1);
    i <= Math.min(totalPages - 1, page + 1);
    i++
  ) {
    pages.push(i);
  }
  if (page < totalPages - 2) pages.push("ellipsis");
  pages.push(totalPages);
  return pages;
}

export type WindowAlign = "near" | "center";

/**
 * Sliding window of page numbers.
 * - `near` matches `getVisiblePageNumbers` (talent search, search prompt).
 * - `center` matches the jobs list table footer.
 */
export function buildWindowPages(
  page: number,
  totalPages: number,
  maxVisible = 4,
  align: WindowAlign = "near",
): number[] {
  const pages: number[] = [];
  let start: number;
  let end: number;
  if (align === "center") {
    start = Math.max(1, page - Math.floor(maxVisible / 2));
    end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
  } else {
    start = Math.max(1, page - 1);
    end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
  }
  for (let i = start; i <= end; i++) pages.push(i);
  return pages;
}

/**
 * Upload-area pattern: pages `1..min(maxLeading,total)`, then ellipsis + last when `totalPages > maxLeading`.
 */
export function buildUploadLeadingPages(
  totalPages: number,
  maxLeading = 5,
): EllipsisPageItem[] {
  if (totalPages <= maxLeading) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  return [
    ...Array.from({ length: maxLeading }, (_, i) => i + 1),
    "ellipsis",
    totalPages,
  ];
}
